import React, { useState, useEffect } from 'react';
import { api } from './api/api';

const fmtNum = (v, digits = 2) => v == null ? '—' : Number(v).toFixed(digits);

// ── Energy tab ──────────────────────────────────────────────────────────

const EnergyTable = ({ data, loading, error }) => {
  if (loading) return <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>;
  if (!data) return null;

  const rows = [...(data.rows || [])].sort((a, b) => {
    const aHas = a.actualEnergy != null, bHas = b.actualEnergy != null;
    return aHas === bHas ? 0 : aHas ? -1 : 1;
  });

  return (
    <div className="fb-card">
      <div className="fb-card-header">
        <span className="fb-card-title">Energy — S{data.season} R{data.raceNumber} · {data.trackName}</span>
      </div>
      <div className="fb-table-wrap">
        <table className="fb-table fb-table-sm">
          <thead>
            <tr>
              <th>Manager</th><th>Risk (CT)</th><th>Predicted</th><th>Actual</th>
              <th>Error (abs)</th><th>Error (%)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const hasActual = r.actualEnergy != null;
              return (
                <tr key={r.userId} style={!hasActual ? { opacity: 0.5 } : undefined}>
                  <td style={{ fontWeight: 600 }}>{r.managerName}</td>
                  <td>{r.ctUsed ?? '—'}</td>
                  <td style={{ color: r.predictedEnergy > 100 ? 'var(--c-error)' : undefined }}>
                    {fmtNum(r.predictedEnergy)}
                    {r.predictedEnergyRaw != null && (
                      <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--c-text-muted)' }} title="Raw prediction, before the local correction — reference only, not used for the error columns.">
                        raw {fmtNum(r.predictedEnergyRaw)}
                      </div>
                    )}
                  </td>
                  <td>{hasActual ? fmtNum(r.actualEnergy) : 'awaiting analysis'}</td>
                  <td>{hasActual ? fmtNum(r.absError) : '—'}</td>
                  <td>{hasActual ? `${fmtNum(r.pctError)}%` : '—'}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: 20 }}>No data.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Tyre wear tab ───────────────────────────────────────────────────────

const TyreTable = ({ data, loading, error }) => {
  if (loading) return <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>;
  if (!data) return null;

  const rows = data.rows || [];

  return (
    <div className="fb-card">
      <div className="fb-card-header">
        <span className="fb-card-title">Tyre Wear — S{data.season} R{data.raceNumber} · {data.trackName}</span>
      </div>
      <div className="fb-table-wrap">
        <table className="fb-table fb-table-sm" style={{ whiteSpace: 'nowrap' }}>
          <thead>
            <tr>
              <th rowSpan={2}>Manager</th>
              <th rowSpan={2}>Compound</th>
              <th rowSpan={2}>Actual Wear</th>
              <th colSpan={3} style={{ textAlign: 'center' }}>V1</th>
              <th colSpan={3} style={{ textAlign: 'center' }}>V2</th>
              <th colSpan={3} style={{ textAlign: 'center' }}>Corrected</th>
            </tr>
            <tr>
              <th>Pred.</th><th>Err (abs)</th><th>Err (%)</th>
              <th>Pred.</th><th>Err (abs)</th><th>Err (%)</th>
              <th>Pred.</th><th>Err (abs)</th><th>Err (%)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const hasActual = r.actualWear != null;
              return (
                <tr key={r.userId} style={!hasActual ? { opacity: 0.5 } : undefined}>
                  <td style={{ fontWeight: 600 }}>{r.managerName}</td>
                  <td>{r.actualCompound ?? '—'}</td>
                  <td>{hasActual ? fmtNum(r.actualWear) : 'awaiting analysis'}</td>
                  <td>{fmtNum(r.predictedV1)}</td><td>{fmtNum(r.absErrorV1)}</td><td>{r.pctErrorV1 != null ? `${fmtNum(r.pctErrorV1)}%` : '—'}</td>
                  <td>{fmtNum(r.predictedV2)}</td><td>{fmtNum(r.absErrorV2)}</td><td>{r.pctErrorV2 != null ? `${fmtNum(r.pctErrorV2)}%` : '—'}</td>
                  <td>{fmtNum(r.predictedCorrected)}</td><td>{fmtNum(r.absErrorCorrected)}</td><td>{r.pctErrorCorrected != null ? `${fmtNum(r.pctErrorCorrected)}%` : '—'}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={12} style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: 20 }}>No data.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Q1 lap time tab ─────────────────────────────────────────────────────

const Q1Table = ({ data, loading, error }) => {
  if (loading) return <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>;
  if (!data) return null;

  const rows = [...(data.rows || [])].sort((a, b) => {
    const aHas = a.actualMs != null, bHas = b.actualMs != null;
    return aHas === bHas ? 0 : aHas ? -1 : 1;
  });

  return (
    <div className="fb-card">
      <div className="fb-card-header">
        <span className="fb-card-title">Q1 — S{data.season} R{data.raceNumber} · {data.trackName}</span>
      </div>
      <div className="fb-table-wrap">
        <table className="fb-table fb-table-sm">
          <thead>
            <tr>
              <th>Manager</th><th>Compound</th><th>Actual Q1</th><th>Predicted</th>
              <th>Error (abs)</th><th>Error (%)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const hasActual = r.actualMs != null;
              const hasPrediction = r.predictedMs != null;
              return (
                <tr key={r.userId} style={!hasActual ? { opacity: 0.5 } : undefined}>
                  <td style={{ fontWeight: 600 }}>{r.managerName}</td>
                  <td>{r.actualCompound ?? '—'}</td>
                  <td>{hasActual ? r.actualFormatted : 'awaiting analysis'}</td>
                  <td>{hasPrediction ? r.predictedFormatted : '—'}</td>
                  <td>{hasPrediction && r.absErrorMs != null ? `${(r.absErrorMs / 1000).toFixed(3)}s` : '—'}</td>
                  <td>{hasPrediction && r.pctError != null ? `${fmtNum(r.pctError)}%` : '—'}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: 20 }}>No data.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────
// Access is already gated at the route level (AdminOnlyRoute in App.js).

const AnalysisResults = () => {
  const [races, setRaces] = useState([]);
  const [racesLoading, setRacesLoading] = useState(true);
  const [racesError, setRacesError] = useState(null);
  const [selectedRace, setSelectedRace] = useState(null);
  const [tab, setTab] = useState('energy');

  const [energyData, setEnergyData] = useState(null);
  const [tyreData, setTyreData] = useState(null);
  const [q1Data, setQ1Data] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setRacesLoading(true);
      setRacesError(null);
      try {
        const res = await api.get('/analysis-results/races');
        const sorted = (res.data || []).slice().sort((a, b) => b.season - a.season || b.raceNumber - a.raceNumber);
        setRaces(sorted);
        if (sorted.length > 0) setSelectedRace({ season: sorted[0].season, raceNumber: sorted[0].raceNumber });
      } catch (err) {
        setRacesError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load races.');
      } finally {
        setRacesLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedRace) return;
    let cancelled = false;
    const load = async () => {
      setDataLoading(true);
      setDataError(null);
      try {
        const endpoint = tab === 'energy' ? '/analysis-results/energy'
          : tab === 'tyre' ? '/analysis-results/tyre-wear'
          : '/analysis-results/q1-laptime';
        const res = await api.get(`${endpoint}?season=${selectedRace.season}&race=${selectedRace.raceNumber}`);
        if (cancelled) return;
        if (tab === 'energy') setEnergyData(res.data);
        else if (tab === 'tyre') setTyreData(res.data);
        else setQ1Data(res.data);
      } catch (err) {
        if (!cancelled) setDataError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load data.');
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedRace, tab]);

  const raceValue = selectedRace ? `${selectedRace.season}-${selectedRace.raceNumber}` : '';
  const handleRaceChange = (e) => {
    const [season, raceNumber] = e.target.value.split('-').map(Number);
    setSelectedRace({ season, raceNumber });
  };

  const tabStyle = (active) => ({
    padding: '9px 20px', cursor: 'pointer', border: 'none', background: 'none',
    fontWeight: active ? 700 : 400, fontSize: 13,
    color: active ? 'var(--c-accent)' : 'var(--c-text-muted)',
    borderBottom: `2px solid ${active ? 'var(--c-accent)' : 'transparent'}`,
    marginBottom: -2, transition: 'color 0.15s',
  });

  return (
    <>
      <div className="fb-page-header">
        <div className="fb-page-title">Analysis Results</div>
        <div className="fb-page-sub">Pre-race predictions vs post-race actuals</div>
      </div>

      <div className="fb-card">
        <div className="fb-card-body" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label className="fb-label" style={{ marginBottom: 0 }}>Race</label>
          {racesLoading ? (
            <span style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Loading races…</span>
          ) : racesError ? (
            <span style={{ color: 'var(--c-error)', fontSize: 13 }}>{racesError}</span>
          ) : races.length === 0 ? (
            <span style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>
              No races yet — predictions start from the next upcoming race.
            </span>
          ) : (
            <select className="fb-select" style={{ minWidth: 340 }} value={raceValue} onChange={handleRaceChange}>
              {races.map(r => (
                <option key={`${r.season}-${r.raceNumber}`} value={`${r.season}-${r.raceNumber}`}>
                  S{r.season} R{r.raceNumber} — {r.trackName} ({r.comparedCount}/{r.managerCount} compared)
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '2px solid var(--c-border)', marginBottom: 16, marginTop: 16 }}>
        <button style={tabStyle(tab === 'energy')} onClick={() => setTab('energy')}>Energy Analysis</button>
        <button style={tabStyle(tab === 'tyre')} onClick={() => setTab('tyre')}>Tyre Wear Analysis</button>
        <button style={tabStyle(tab === 'q1')} onClick={() => setTab('q1')}>Q1 Analysis</button>
      </div>

      {tab === 'energy' && <EnergyTable data={energyData} loading={dataLoading} error={dataError} />}
      {tab === 'tyre' && <TyreTable data={tyreData} loading={dataLoading} error={dataError} />}
      {tab === 'q1' && <Q1Table data={q1Data} loading={dataLoading} error={dataError} />}
    </>
  );
};

export default AnalysisResults;
